import React, { useState } from 'react';
import { notification, Space, Tag } from 'antd';
import type { TablePaginationConfig, TableProps } from 'antd';
import { IUser, ISearchUserReq, ISearchUserRes } from '@/interfaces/user';
import { EditFilled, DeleteFilled, KeyOutlined } from '@ant-design/icons';
import { IDataModal } from '@/interfaces/dataModal';
import DeleteConfirmModal from '@/components/modal/confirm-delete';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import TableComponent from '@/components/table';
import { deleteUser } from '@/adapters/user';
import { getUserRoleName } from '@/helpers/roleHelper';

interface IProps {
  loading: boolean;
  criteria: ISearchUserReq;
  user: ISearchUserRes;
  onDelete: (id: number) => void;
  onTableChange: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<IUser> | SorterResult<IUser>[],
  ) => void;
  onEdit: (user: IUser) => void;
  onResetPassword: (user: IUser) => void;
}

const _userModal: IDataModal<IUser> = {
  open: false,
};

const App = ({
  loading,
  criteria,
  user,
  onDelete,
  onTableChange,
  onEdit,
  onResetPassword,
}: IProps) => {
  const [deleteModal, setDeleteModal] = useState<IDataModal<IUser>>(_userModal);

  const onDeleteUser = async () => {
    try {
      await deleteUser(deleteModal?.id?.toString() || '');
      notification.success({
        message: 'ยกเลิกผู้ใช้งานสำเร็จ',
      });
      onDelete(Number(deleteModal?.id || 0));
      setDeleteModal({ ..._userModal });
    } catch (error) {
      console.log(error);
    }
  };

  const columns: TableProps<IUser>['columns'] = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
      width: 40,
      align: 'center',
      className: 'text-center',
      render: (_, rec, i) => <Space>{i + 1}</Space>,
    },
    {
      title: 'USERNAME',
      dataIndex: 'userName',
      key: 'userName',
      className: 'font-bold',
      width: 140,
    },
    {
      title: 'ชื่อ',
      dataIndex: 'firstName',
      key: 'firstName',
      className: 'font-bold',
      width: 140,
    },
    {
      title: 'นามสกุล',
      dataIndex: 'lastName',
      key: 'lastName',
      className: 'font-bold',
      width: 140,
    },
    {
      title: 'ชื่อเล่น',
      dataIndex: 'nickName',
      key: 'nickName',
      fixed: 'left',
      className: 'font-medium',
      width: 80,
    },
    {
      title: 'เบอร์โทร',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      className: 'font-medium',
    },
    {
      title: 'อีเมล์',
      dataIndex: 'email',
      key: 'email',
      className: 'font-medium',
    },
    {
      title: 'BU',
      dataIndex: 'bu',
      key: 'bu',
      className: 'font-medium',
    },
    {
      title: 'สิทธิ์',
      dataIndex: 'role',
      key: 'role',
      className: 'font-medium',
      render: _ => {
        const roleName = getUserRoleName(_);
        return <div>{roleName}</div>;
      },
    },
    {
      title: '2FA',
      dataIndex: 'superAdmin',
      key: 'superAdmin',
      width: 100,
      align: 'center',
      render: (_: boolean) => {
        return (
          <Tag color={_ ? `red` : 'green'} className="min-w-[60px] text-center">
            {_ ? 'ปิดงานใข้งาน' : 'ใช้งานอยู่'}
          </Tag>
        );
      },
    },
    {
      title: 'สถานะ',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      align: 'center',
      render: (_: boolean) => {
        return (
          <Tag color={_ ? `green` : 'red'} className="min-w-[60px] text-center">
            {_ ? 'เปิดใช้งาน' : 'ปิดงานใข้งาน'}
          </Tag>
        );
      },
    },
    {
      dataIndex: 'action',
      key: 'action',
      width: 120,
      render: (_, rec) => (
        <div className="flex flex-row gap-4 items-center w-full justify-center">
          <DeleteFilled
            className="text-red-700"
            onClick={() =>
              setDeleteModal({
                open: true,
                item: rec,
                id: rec.id,
              })
            }
          />
          <KeyOutlined
            className="text-orange-500"
            onClick={() => onResetPassword(rec)}
          />
          <EditFilled className="text-blue-500" onClick={() => onEdit(rec)} />
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <TableComponent<IUser>
        columns={columns}
        criteria={criteria}
        data={user}
        loading={loading}
        onTableChange={onTableChange}
        scroll={true}
        scrollLength={1500}
      />
      <DeleteConfirmModal
        content={`คุณต้องการยกเลิกผู้ใช้งาน '${deleteModal?.item?.firstName}' ใช่หรือไม่`}
        handleCancel={() => {
          setDeleteModal({ ..._userModal });
        }}
        handleOk={() => {
          onDeleteUser();
        }}
        open={deleteModal?.open}
        title={`ผู้ใช้งาน '${deleteModal?.item?.firstName}'`}
      />
    </div>
  );
};

export default App;
